// Live requirement checklist shown during the login page's forced-reset
// step (C3). Ported from reference/scopely-frontend/src/components/
// PasswordRequirements.tsx, minus the HIBP breach-check row (see
// ../lib/passwordPolicy.ts for why).
"use client";

import { useMemo } from "react";

import { analyzePasswordPolicy, MIN_PASSWORD_LENGTH, type PasswordIdentity } from "../lib/passwordPolicy";

interface PasswordRequirementsProps extends PasswordIdentity {
  password: string;
  className?: string;
}

interface RequirementItem {
  ok: boolean;
  label: string;
  /**
   * The reference's per-row sub-line (`detail` on its RequirementItem). The
   * port originally dropped it, which silently removed the identity rule's
   * explanation from all four screens that render this control while the
   * header claimed only the breach row was missing. Restored 2026-08-19.
   */
  detail?: string;
}

export function PasswordRequirements({
  password,
  email,
  username,
  firstName,
  lastName,
  className,
}: PasswordRequirementsProps) {
  const analysis = useMemo(
    () => analyzePasswordPolicy(password, { email, username, firstName, lastName }),
    [password, email, username, firstName, lastName],
  );

  const rows: RequirementItem[] = [
    { ok: analysis.meetsLength, label: `At least ${MIN_PASSWORD_LENGTH} characters` },
    { ok: analysis.notNumeric, label: "Not all numeric" },
  ];
  if (analysis.hasIdentityHints) {
    rows.push({
      ok: !analysis.resemblesIdentity,
      label: "Doesn't resemble your email or name",
      // Verbatim from the reference — it has no network dependency, unlike
      // the breach row, so there was never a reason to omit it.
      detail:
        "Compared against the email, username, first name, and last name you entered.",
    });
  }

  return (
    <ul className={className} aria-live="polite">
      {rows.map((row) => (
        <li
          key={row.label}
          className={row.ok ? "text-xs text-green-700" : "text-xs text-ink-light"}
        >
          {row.label}
          {row.detail ? (
            <span className="block text-[11px] text-ink-faint">{row.detail}</span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
